import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Form, FormRow } from '@/components/Form'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'

import styles from './EstimatedIrsPaymentDialog.module.css'

export interface EstimatedIrsPaymentDialogProps {
  showing: boolean
  insufficientDocuments: boolean
  insufficientDocumentsInput: ReactNode
  federalAmountInput: ReactNode
  /**
   * One `EstimatedIrsPaymentStateEntryRow` per state entry.
   */
  children?: ReactNode
  isSubmitPending: boolean
  isSubmitDisabled: boolean
  onClose: () => unknown
  onAddState: () => unknown
  onSubmit: () => unknown
}

export function EstimatedIrsPaymentDialog({
  showing,
  insufficientDocuments,
  insufficientDocumentsInput,
  federalAmountInput,
  children,
  isSubmitPending,
  isSubmitDisabled,
  onClose,
  onAddState,
  onSubmit,
}: EstimatedIrsPaymentDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Extension Accepted</ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetContent>
        <ModalSheetContentBlock expanded>
          <Form>
            <FormRow>{insufficientDocumentsInput}</FormRow>

            {!insufficientDocuments && (
              <>
                <FormRow>{federalAmountInput}</FormRow>

                {children}

                <FormRow>
                  <Button
                    label="Add State"
                    variant="secondary"
                    size="wide-slim"
                    onPress={onAddState}
                    testId="estimated-tax-payment-add-state"
                  />
                </FormRow>
              </>
            )}
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Cancel"
            variant="default"
            isDisabled={isSubmitPending}
            onPress={onClose}
          />
          <Button
            label="Accept Extension"
            variant="primary"
            testId="estimated-tax-payment-submit-button"
            isPending={isSubmitPending}
            isDisabled={isSubmitDisabled}
            onPress={onSubmit}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface EstimatedIrsPaymentStateEntryRowProps {
  index: number
  stateInput: ReactNode
  amountInput: ReactNode
  onRemove: () => void
}

export function EstimatedIrsPaymentStateEntryRow({
  index,
  stateInput,
  amountInput,
  onRemove,
}: EstimatedIrsPaymentStateEntryRowProps) {
  return (
    <FormRow>
      <div className={styles.stateEntryRow}>
        <div className={styles.stateEntryField}>{stateInput}</div>
        <div className={styles.stateEntryField}>{amountInput}</div>
        <div className={styles.stateEntryRemove}>
          <IconButton
            name="close"
            label="Remove state"
            onPress={onRemove}
            testId={`estimated-tax-payment-remove-state-${index}`}
          />
        </div>
      </div>
    </FormRow>
  )
}
